import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { withTranslation } from 'react-i18next';
import TextField from '@material-ui/core/TextField';
import { DropDown } from '../shared/Elements';
import { getGoalTime, validateHHMMSS } from '../../utils/date';
import { Loading } from '../shared/Loading';
import { styles } from './styles';

export class EventCalendarFormComponent extends Component {

  static propTypes = {
    onChange: PropTypes.func.isRequired,
    eventName: PropTypes.string.isRequired,
    masterId: PropTypes.number.isRequired,
    events: PropTypes.array.isRequired,
    raceGoals: PropTypes.string,
    isStarter: PropTypes.bool.isRequired,
    intl: PropTypes.object,
    t: PropTypes.func
  };

  static defaultProps = {
    raceGoals: '',
    goalTime: ''
  };

  state = {
    EventID: this.props.eventId,
    EventCourseID: this.props.eventCourseId,
    CourseID: this.props.courseId || this.getCourseId(this.props.events, this.props.eventId, this.props.eventCourseId),
    RaceGoals: this.props.raceGoals,
    TargetTime: !!this.props.goalTime ? getGoalTime(this.props.goalTime) : '',
    validInput: validateHHMMSS(getGoalTime(this.props.goalTime)) || (this.props.goalTime === '')
  };

  componentDidMount() {
    this.props.onChange(this.state);
  }

  getData = (eventId, eventCourseId, events) => {
    const courseId = this.getCourseId(events, eventId, eventCourseId);

    return {EventID: eventId, EventCourseID: eventCourseId, CourseID: courseId};
  };

  getCourseId(events, eventId, eventCourseId) {
    const event = (events || []).find(({id}) => id === eventId) || {};
    // we intentionally use double equals on course id below to be safe if it ends up being string compared to int
    const courseObj = (event.courses || []).find(({eventCourseID}) => eventCourseID == eventCourseId) || (event.courses || [])[0];

    return (courseObj || {}).courseID;
  }

  handleChange(key, event) {
    this.setState(
      {
        [key]: event.target.value
      },
      () => this.props.onChange(this.state)
    );
  }

  handleTimeChange(event) {
    const time = event.target.value;

    this.setState(
      {
        TargetTime: time,
        validInput: validateHHMMSS(time) || (time === '')
      },
      () => this.props.onChange(this.state)
    );
  }

  buildEventOptions = () => this.props.events.map(({date, id}) => ({
    value: id,
    label: this.props.intl.formatDate(new Date(date), {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    })
  }));

  buildCoursesOptions = () => this.props.events
    .find(({id}) => id === this.state.EventID)
    .courses.map(({courseName, eventCourseID}) => ({
      value: eventCourseID,
      label: courseName
    }));

  // NOTE: EVENT AND RACE CHANGE CAN ONLY OCCUR FROM THE ELP
  onEventChange = (e) => {
    const eventId = e.target.value;
    if(eventId !== this.state.EventID) {
      this.setState(
        this.getData(eventId, null, this.props.events),
        () => this.props.onChange(this.state));
    }
  };

  onRaceChange = (e) => {
    const {EventID} = this.state;
    const eventCourseId = e.target.value;

    this.setState(
      this.getData(EventID, eventCourseId, this.props.events),
      () => {
        this.props.onChange(this.state)
      });
  };

  isLoading = () => {
    const {events} = this.props;
    const {EventID} = this.state;

    return events === null || !EventID;
  };

  render(){
    const {
      isStarter,
      canEditEvent,
      t
    } = this.props;

    if(this.isLoading()) {
      return (
        <div style={styles.loading}>
          <Loading timeoutMessage={t('Still thinking...')}/>
        </div>
      );
    }

    const {
      EventID,
      EventCourseID,
      validInput
    } = this.state;

    const eventOptions = this.buildEventOptions();
    const eventValue = EventID || eventOptions[0].value;
    const courseOptions = this.buildCoursesOptions();
    const courseValue = EventCourseID || courseOptions[0].value;

    const bodyMessage = isStarter
      ? t('Edit or remove your start list entry below.')
      : t('Enter your information below to add yourself to the start list.');
    const dropDownStyle = {
      marginBottom: '24px'
    };
    const dropDownInputStyle = {
      width: '300px'
    }

    return (
      <div>
        <div style={styles.startListBody}>
          {bodyMessage}
          <div id='event-calendar-message' style={styles.addToCalNote}>
            {t(
              'This does not officially register you for the race. The Athlinks start'
              + ' list is not the official race start list.'
            )}
          </div>
        </div>
        <div className="col-8">
          <div className='row mx-0'>
            <div className='pl-0 col-7'>
              <div style={dropDownStyle}>
                <DropDown
                  id='event'
                  label={t('Event')}
                  value={eventValue}
                  onChange={this.onEventChange}
                  options={eventOptions}
                  disabled={!canEditEvent}
                  inputStyle={dropDownInputStyle}
                />
              </div>
              <div style={dropDownStyle}>
                <DropDown
                  id='eventCourses'
                  label={t('Which race will you be participating in?')}
                  value={parseInt(courseValue)}
                  onChange={this.onRaceChange}
                  options={courseOptions}
                  disabled={!canEditEvent}
                  inputStyle={dropDownInputStyle}
                />
              </div>
            </div>
          </div>
          <div>
            <label style={styles.label}>
              {t('What is your goal time?')}
            </label>
            <br/>
            <TextField
              key="TargetTime"
              id="TargetTime"
              className='TargetTime'
              placeholder="hh:mm:ss"
              onChange={this.handleTimeChange.bind(this)}
              fullWidth={false}
              value={this.state.TargetTime}
            />
          </div>
          {
            !validInput &&
            <div style={styles.invalidInput} id='invalid-goal-time'>
              {t('Invalid format (hh:mm:ss)')}
            </div>
          }
          <div style={{ marginTop: 20}}>
            <label style={styles.label}>
              {t('Do you have any race goals or something you\'d like to '
                + 'tell your followers?')}
            </label>
            <br/>
            <TextField
              key="RaceGoals"
              id="RaceGoal"
              placeholder={t('ex. Finish strong!')}
              onChange={this.handleChange.bind(this, 'RaceGoals')}
              fullWidth={true}
              value={this.state.RaceGoals}
            />
          </div>
        </div>
      </div>
    );
  }
}

export const EventCalendarForm = withTranslation()(injectIntl(EventCalendarFormComponent));



/** WEBPACK FOOTER **
 ** ./components/eventCalendar/EventCalendarForm.js
 **/